import React, { useEffect } from 'react';
import setSeo from '../utils/seo.js';
import { Link } from 'react-router-dom';
import PageHero3D from '../components/PageHero3D.jsx';
import BackToTop from '../components/BackToTop.jsx';
import blog1 from '../assets/blog1.webp';
import blog2 from '../assets/blog2.webp';
import blog3 from '../assets/blog3.webp';
import blog4 from '../assets/blog4.webp';

/**
 * BlogPage lists practical blog posts about CRM, automation, and customer support.
 * Each post card contains an image, title and summary. A call to action invites
 * users to read more. The page hero carries the particle background.
 */
const posts = [
  {
    id: 1,
    title: 'Why Choose SuiteCRM in the age of AI?',
    excerpt: 'Discover how SuiteCRM remains relevant in the AI era with its adaptability, integration potential, and cost-effectiveness for modern businesses.',
    image: blog1,
    slug: '/blog/suitecrm-ai-age'
  },
  {
    id: 2,
    title: 'Modernizing Business Operations: How Generative AI + Automation Can Upgrade Your Business',
    excerpt: 'Learn how Generative AI and workflow automation transform business operations through RAG, agent orchestration, and privacy-first self-hosting approaches.',
    image: blog2,
    slug: '/blog/modernizing-business-operations'
  },
  {
    id: 3,
    title: 'Why CRM is a Process, Not Just Software: A Beginner\'s Guide',
    excerpt: 'Learn why CRM is fundamentally a process and discover why SuiteCRM is the perfect starting point for businesses new to CRM.',
    image: blog3,
    slug: '/blog/crm-process-not-software'
  },
  {
    id: 4,
    title: '10 Proven Strategies to Boost Donor Retention Effortlessly with SuiteCRM',
    excerpt: 'Turn occasional donors into lifelong advocates with these proven strategies and SuiteCRM\'s powerful donor engagement tools.',
    image: blog4,
    slug: '/blog/donor-retention-suitecrm'
  },
  {
    id: 5,
    title: 'OpenClaw SuiteCRM Integration: Building Secure AI Agents Without Giving Away Your Identity',
    excerpt: 'A secure, self-hosted integration blueprint for OpenClaw + SuiteCRM with strict boundaries, API-scoped permissions, and event-driven automation.',
    image: blog2,
    slug: '/blog/openclaw-suitecrm-integration'
  }
];

const BlogPage = () => {
  useEffect(() => {
    setSeo({
      title: 'Blog | Aakeeo Technologies - CRM & AI Automation Insights',
      description: 'Expert articles on CRM implementation, SuiteCRM best practices, AI automation, and business process optimization. Get practical insights, tutorials, and strategies from industry leaders.',
      keywords: ['CRM blog', 'SuiteCRM tutorials', 'AI automation guides', 'CRM best practices', 'workflow automation', 'business process optimization'],
      og: {
        title: 'CRM & AI Technology Blog | Aakeeo Technologies',
        description: 'Deep dive into CRM best practices, AI automation strategies, and business process optimization. Expert insights and practical guides.',
        type: 'website'
      },
      canonical: window.location.href,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        'name': 'Aakeeo Technologies Blog',
        'description': 'Expert insights on CRM implementation, AI automation, and business optimization',
        'url': 'https://aakeeo.com/blog',
        'publisher': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies',
          'logo': {
            '@type': 'ImageObject',
            'url': 'https://aakeeo.com/Aakeeo_favicon.png'
          }
        }
      }
    });
  }, []);

  return (
    <div className="blog-page">
      <PageHero3D 
        title="Practical automation & CRM insights" 
        subtitle="Playbooks, tutorials, and real outcomes from the field." 
        accent="#0077b6"
      />
      <section className="section reveal">
        <div className="section-header">
          <h2>Latest Articles</h2>
          <p>Stay up to date with the latest trends, best practices and thought leadership from the Aakeeo team.</p>
        </div>
        <div className="blog-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
          {posts.map(post => (
            <article key={post.id} className="blog-card" style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '20px', overflow: 'hidden', backdropFilter: 'blur(8px)', boxShadow: '0 10px 20px rgba(0,0,0,0.06)', transition: 'transform 0.2s ease' }}>
              <div className="blog-card-image" style={{ height: '180px', overflow: 'hidden' }}>
                <img src={post.image} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1.2rem', color: 'var(--secondary-color)', marginBottom: '0.5rem' }}>{post.title}</h3>
                <p style={{ color: 'var(--text-light)', fontSize: '0.95rem', marginBottom: '1rem' }}>{post.excerpt}</p>
                <Link 
                  to={post.slug} 
                  className="btn-primary" 
                  style={{ fontSize: '0.9rem', padding: '0.6rem 1.4rem' }}
                >
                  Read More
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
      <BackToTop />
    </div>
  );
};

export default BlogPage;
